import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, FileText, X, CheckCircle, Clock, AlertTriangle, UserX, Send, Mail, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { PageHeader } from '../components/admin';
import { fmtTRY } from '../utils/formatCurrency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: 'vendor', label: 'Contracts', label_tr: 'Sözleşmeler' },
  { id: 'student', label: 'Student Contracts', label_tr: 'Öğrenci Sözleşmeleri' },
  { id: 'cancellations', label: 'Cancellations', label_tr: 'İptaller & İadeler' },
  { id: 'inactive', label: 'Inactive Students', label_tr: 'İnaktif Öğrenciler' },
];

function VendorContracts({ lang, authHeaders }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: '', party: '', type: 'instructor', status: 'draft', start_date: '', end_date: '', amount: 0, notes: '' });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/contracts`, { headers: authHeaders() });
      setItems(r.data.contracts);
    } catch (err) {
      console.error(err);
      toast.error(getApiErrorMessage(err, lang));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.party) {
      toast.warning(lang === 'tr' ? 'Başlık ve taraf zorunlu' : 'Title and party required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/contracts`, form, { headers: authHeaders() });
      toast.success(lang === 'tr' ? 'Sözleşme oluşturuldu' : 'Contract created');
      setShowForm(false);
      setForm({ title: '', party: '', type: 'instructor', status: 'draft', start_date: '', end_date: '', amount: 0, notes: '' });
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contracts/${id}`, { status }, { headers: authHeaders() });
      toast.success(lang === 'tr' ? 'Durum güncellendi' : 'Status updated');
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      load();
    }
  };
  const statusColors = { draft: 'bg-gray-100 text-gray-600', active: 'bg-green-100 text-green-600', expired: 'bg-red-100 text-red-600', terminated: 'bg-orange-100 text-orange-600' };
  const statusLabels = { draft: { tr: 'Taslak', en: 'Draft' }, active: { tr: 'Aktif', en: 'Active' }, expired: { tr: 'Süresi Doldu', en: 'Expired' }, terminated: { tr: 'Sonlandırıldı', en: 'Terminated' } };
  const statusLabel = (s) => (statusLabels[s]?.[lang] ?? s);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-[var(--text-muted)] font-semibold">{items.length} {lang === 'tr' ? 'sözleşme' : 'contracts'}</span>
        <button onClick={() => setShowForm(true)} className="btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="add-contract-btn"><Plus size={16} /> {lang === 'tr' ? 'Yeni Sözleşme' : 'New Contract'}</button>
      </div>
      {showForm && (
        <div className="card-clay p-5 mb-4 space-y-3">
          <div className="flex justify-between"><h3 className="font-bold">{lang === 'tr' ? 'Yeni Sözleşme' : 'New Contract'}</h3><button onClick={() => setShowForm(false)}><X size={18} /></button></div>
          <div className="grid md:grid-cols-2 gap-3">
            <input placeholder={lang === 'tr' ? 'Sözleşme başlığı' : 'Contract title'} value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm" />
            <input placeholder={lang === 'tr' ? 'Taraf (isim/şirket)' : 'Party (name/company)'} value={form.party} onChange={e => setForm({...form, party: e.target.value})} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm">
              <option value="instructor">{lang === 'tr' ? 'Eğitmen' : 'Instructor'}</option>
              <option value="vendor">{lang === 'tr' ? 'Tedarikçi' : 'Vendor'}</option>
              <option value="partnership">{lang === 'tr' ? 'Ortaklık' : 'Partnership'}</option>
            </select>
            <input type="number" placeholder={lang === 'tr' ? 'Tutar' : 'Amount'} value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value)||0})} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
            <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
            <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
          </div>
          <textarea placeholder={lang === 'tr' ? 'Notlar...' : 'Notes...'} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" />
          <button onClick={save} disabled={submitting} className="btn-success px-6 py-2 text-sm disabled:opacity-50" data-testid="save-contract-btn">{submitting ? (lang === 'tr' ? 'Oluşturuluyor...' : 'Creating...') : (lang === 'tr' ? 'Oluştur' : 'Create')}</button>
        </div>
      )}
      <div className="space-y-3">
        {items.map(c => (
          <div key={c.contract_id} className="card-clay p-4 flex items-center gap-4">
            <FileText size={20} className="text-[var(--brand-primary)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1"><h4 className="font-bold text-sm">{c.title}</h4><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${statusColors[c.status] || 'bg-gray-100'}`}>{statusLabel(c.status)}</span></div>
              <p className="text-xs text-[var(--text-muted)]">{c.party} {c.amount > 0 && `| ${fmtTRY(c.amount)}`}</p>
            </div>
            <select value={c.status} onChange={e => updateStatus(c.contract_id, e.target.value)} className="px-2 py-1 rounded border text-xs font-bold" data-testid={`status-${c.contract_id}`}>
              <option value="draft">{lang === 'tr' ? 'Taslak' : 'Draft'}</option>
              <option value="active">{lang === 'tr' ? 'Aktif' : 'Active'}</option>
              <option value="expired">{lang === 'tr' ? 'Süresi Doldu' : 'Expired'}</option>
              <option value="terminated">{lang === 'tr' ? 'Sonlandırıldı' : 'Terminated'}</option>
            </select>
          </div>
        ))}
        {items.length === 0 && <div className="card-clay p-8 text-center text-[var(--text-muted)]"><FileText size={32} className="mx-auto mb-2" /><p className="font-semibold text-sm">{lang === 'tr' ? 'Henüz sözleşme yok' : 'No contracts'}</p></div>}
      </div>
    </div>
  );
}

function StudentContracts({ lang, authHeaders }) {
  const toast = useToast();
  const [data, setData] = useState({ signed: [], not_signed: [], signed_count: 0, pending_count: 0 });
  const [subTab, setSubTab] = useState('signed');

  useEffect(() => {
    axios.get(`${API}/admin/contracts/signed`, { headers: authHeaders() })
      .then(r => setData(r.data))
      .catch(err => {
        console.error(err);
        toast.error(getApiErrorMessage(err, lang));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card-clay p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center"><CheckCircle size={20} className="text-green-600" /></div>
          <div><p className="text-2xl font-bold text-green-600">{data.signed_count}</p><p className="text-xs text-[var(--text-muted)] font-semibold">{lang === 'tr' ? 'Onaylayan' : 'Signed'}</p></div>
        </div>
        <div className="card-clay p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center"><Clock size={20} className="text-orange-600" /></div>
          <div><p className="text-2xl font-bold text-orange-600">{data.pending_count}</p><p className="text-xs text-[var(--text-muted)] font-semibold">{lang === 'tr' ? 'Bekleyen' : 'Pending'}</p></div>
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setSubTab('signed')} className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${subTab === 'signed' ? 'bg-green-500 text-white' : 'bg-gray-100 text-[var(--text-muted)] hover:bg-gray-200'}`} data-testid="signed-tab">
          <CheckCircle size={12} className="inline mr-1" /> {lang === 'tr' ? 'Onaylayanlar' : 'Signed'} ({data.signed_count})
        </button>
        <button onClick={() => setSubTab('pending')} className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${subTab === 'pending' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-[var(--text-muted)] hover:bg-gray-200'}`} data-testid="pending-tab">
          <Clock size={12} className="inline mr-1" /> {lang === 'tr' ? 'Bekleyenler' : 'Pending'} ({data.pending_count})
        </button>
      </div>

      {subTab === 'signed' ? (
        <div className="card-clay overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Öğrenci' : 'Student'}</th>
                <th className="text-left p-3 font-bold">Email</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Imza Tarihi' : 'Signed Date'}</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Sözleşme No' : 'Contract No'}</th>
              </tr>
            </thead>
            <tbody>
              {data.signed.map((c, i) => (
                <tr key={c.contract_id || i} className="border-t border-[var(--border-light)] hover:bg-gray-50" data-testid={`signed-row-${i}`}>
                  <td className="p-3 font-bold text-xs">{c.user_name}</td>
                  <td className="p-3 text-xs text-[var(--text-muted)]">{c.user_email}</td>
                  <td className="p-3 text-center text-xs">{c.signed_at ? new Date(c.signed_at).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-green-100 text-green-600 rounded text-[10px] font-bold">{c.contract_id}</span></td>
                </tr>
              ))}
              {data.signed.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[var(--text-muted)] text-sm">{lang === 'tr' ? 'Henuz imzalanan sozlesme yok' : 'No signed contracts'}</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-clay overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Öğrenci' : 'Student'}</th>
                <th className="text-left p-3 font-bold">Email</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Kayıt Tarihi' : 'Registered'}</th>
                <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Durum' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {data.not_signed.map((u, i) => (
                <tr key={u.user_id || i} className="border-t border-[var(--border-light)] hover:bg-gray-50" data-testid={`pending-row-${i}`}>
                  <td className="p-3 font-bold text-xs">{u.name}</td>
                  <td className="p-3 text-xs text-[var(--text-muted)]">{u.email}</td>
                  <td className="p-3 text-center text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded text-[10px] font-bold">{lang === 'tr' ? 'Bekliyor' : 'Pending'}</span></td>
                </tr>
              ))}
              {data.not_signed.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[var(--text-muted)] text-sm">{lang === 'tr' ? 'Tüm öğrenciler sözleşmeyi onaylamış' : 'All students signed'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CancellationsTab({ lang, authHeaders }) {
  const toast = useToast();
  const [data, setData] = useState({ cancellations: [], total: 0, total_refund: 0 });

  useEffect(() => {
    axios.get(`${API}/admin/cancellations`, { headers: authHeaders() })
      .then(r => setData(r.data))
      .catch(err => {
        console.error(err);
        toast.error(getApiErrorMessage(err, lang));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card-clay p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><AlertTriangle size={20} className="text-red-600" /></div>
          <div><p className="text-2xl font-bold text-red-600">{data.total}</p><p className="text-xs text-[var(--text-muted)] font-semibold">{lang === 'tr' ? 'Toplam İptal' : 'Total Cancellations'}</p></div>
        </div>
        <div className="card-clay p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><FileText size={20} className="text-blue-600" /></div>
          <div><p className="text-2xl font-bold text-blue-600">{data.total_refund?.toFixed(0)} TRY</p><p className="text-xs text-[var(--text-muted)] font-semibold">{lang === 'tr' ? 'Toplam Iade' : 'Total Refunds'}</p></div>
        </div>
      </div>

      <div className="card-clay overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Öğrenci' : 'Student'}</th>
              <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Kurs' : 'Course'}</th>
              <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Neden' : 'Reason'}</th>
              <th className="text-right p-3 font-bold">{lang === 'tr' ? 'Iade' : 'Refund'}</th>
              <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Tarih' : 'Date'}</th>
            </tr>
          </thead>
          <tbody>
            {data.cancellations.map((c, i) => (
              <tr key={c.cancel_id || i} className="border-t border-[var(--border-light)] hover:bg-gray-50" data-testid={`cancel-row-${i}`}>
                <td className="p-3">
                  <p className="font-bold text-xs">{c.student_name}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{c.student_email}</p>
                </td>
                <td className="p-3 text-xs font-semibold">{c.course_title}</td>
                <td className="p-3 text-xs text-[var(--text-muted)] max-w-[200px] truncate">{c.reason || '-'}</td>
                <td className="p-3 text-right font-bold text-xs text-red-600">{c.refund_amount > 0 ? `-${c.refund_amount} TRY` : '0 TRY'}</td>
                <td className="p-3 text-center text-xs">{c.cancelled_at ? new Date(c.cancelled_at).toLocaleDateString('tr-TR') : '-'}</td>
              </tr>
            ))}
            {data.cancellations.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[var(--text-muted)] text-sm">{lang === 'tr' ? 'Henüz iptal yok' : 'No cancellations'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InactiveStudents({ lang, authHeaders }) {
  const toast = useToast();
  const [students, setStudents] = useState([]);
  const [days, setDays] = useState(7);
  const [selected, setSelected] = useState([]);
  const [channel, setChannel] = useState('email');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [runningCron, setRunningCron] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/students/inactive?days=${days}`, { headers: authHeaders() });
      setStudents(r.data.inactive_students);
      setSelected([]);
    } catch (err) {
      console.error(err);
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const loadScheduler = async () => {
    try {
      const r = await axios.get(`${API}/admin/scheduler/status`, { headers: authHeaders() });
      setScheduler(r.data);
    } catch (err) {
      console.error(err);
      /* intentionally ignored for UI toast: scheduler status is non-critical overview */
    }
  };

  const runCronNow = async () => {
    setRunningCron(true);
    try {
      const r = await axios.post(`${API}/admin/scheduler/run-now`, {}, { headers: authHeaders() });
      setSendResult({ sent: r.data.sent, total: r.data.total });
      loadScheduler();
      load();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setRunningCron(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadScheduler(); }, [days]);

  const toggleSelect = (uid) => {
    setSelected(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const selectAll = () => {
    if (selected.length === students.length) setSelected([]);
    else setSelected(students.map(s => s.user_id));
  };

  const sendReminders = async () => {
    if (selected.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await axios.post(`${API}/admin/students/send-reminders`, {
        user_ids: selected,
        channel,
        message: message || undefined
      }, { headers: authHeaders() });
      setSendResult(r.data);
      setSelected([]);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSending(false); }
  };

  return (
    <div>
      {/* Scheduler Status Card */}
      <div className="card-clay p-4 mb-4 bg-indigo-50 border-2 border-indigo-200" data-testid="scheduler-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center"><Clock size={20} className="text-indigo-600" /></div>
            <div>
              <h4 className="text-sm font-bold text-indigo-800">{lang === 'tr' ? 'Otomatik Hatirlatma' : 'Auto Reminder'}</h4>
              <p className="text-[10px] text-indigo-600">
                {scheduler?.last_run
                  ? `${lang === 'tr' ? 'Son çalışma' : 'Last run'}: ${new Date(scheduler.last_run.run_at).toLocaleString('tr-TR')} (${scheduler.last_run.sent}/${scheduler.last_run.total})`
                  : (lang === 'tr' ? 'Henüz çalıştırılmadı' : 'Not run yet')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-100 text-green-600 rounded-lg text-[10px] font-bold">{lang === 'tr' ? 'Aktif - Her Pazartesi 09:00' : 'Active - Mon 09:00'}</span>
            <button onClick={runCronNow} disabled={runningCron}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1" data-testid="run-cron-btn">
              <Send size={12} /> {runningCron ? '...' : (lang === 'tr' ? 'Şimdi Çalıştır' : 'Run Now')}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'Son giriş' : 'Last login'}:</span>
          {[3, 7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${days === d ? 'bg-[var(--brand-primary)] text-white' : 'bg-gray-100 text-[var(--text-muted)] hover:bg-gray-200'}`} data-testid={`days-${d}`}>
              {d} {lang === 'tr' ? 'gün' : 'days'}
            </button>
          ))}
        </div>
        <span className="text-sm font-bold text-red-500 ml-auto"><UserX size={14} className="inline mr-1" />{students.length} {lang === 'tr' ? 'inaktif öğrenci' : 'inactive students'}</span>
      </div>

      {/* Send Reminder Bar */}
      {selected.length > 0 && (
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="card-clay p-4 mb-4 bg-blue-50 border-2 border-blue-200">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-blue-700">{selected.length} {lang === 'tr' ? 'öğrenci seçildi' : 'selected'}</span>
            <select value={channel} onChange={e => setChannel(e.target.value)} className="px-3 py-1.5 rounded-lg border-2 border-blue-200 bg-white text-xs font-bold" data-testid="channel-select">
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
            <input value={message} onChange={e => setMessage(e.target.value)}
              placeholder={lang === 'tr' ? 'Özel mesaj (opsiyonel)...' : 'Custom message (optional)...'}
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border-2 border-blue-200 bg-white text-xs font-semibold" data-testid="reminder-message" />
            <button onClick={sendReminders} disabled={sending}
              className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1" data-testid="send-reminders-btn">
              <Send size={12} /> {sending ? '...' : (lang === 'tr' ? 'Hatirlatma Gonder' : 'Send Reminder')}
            </button>
          </div>
          {sendResult && (
            <p className="text-xs font-bold text-green-600 mt-2" data-testid="send-result">
              {sendResult.sent}/{sendResult.total} {lang === 'tr' ? 'başarıyla gönderildi' : 'sent successfully'}
            </p>
          )}
        </motion.div>
      )}

      {/* Student Table */}
      <div className="card-clay overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 w-10"><input type="checkbox" checked={selected.length === students.length && students.length > 0} onChange={selectAll} className="rounded" data-testid="select-all-inactive" /></th>
              <th className="text-left p-3 font-bold">{lang === 'tr' ? 'Öğrenci' : 'Student'}</th>
              <th className="text-left p-3 font-bold">{lang === 'tr' ? 'İletişim' : 'Contact'}</th>
              <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Son Giriş' : 'Last Login'}</th>
              <th className="text-center p-3 font-bold">{lang === 'tr' ? 'Gün' : 'Days'}</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, i) => {
              const lastLogin = s.last_login ? new Date(s.last_login) : null;
              const daysAgo = lastLogin ? Math.floor((Date.now() - lastLogin.getTime()) / 86400000) : '?';
              return (
                <tr key={s.user_id} className="border-t border-[var(--border-light)] hover:bg-gray-50" data-testid={`inactive-row-${i}`}>
                  <td className="p-3"><input type="checkbox" checked={selected.includes(s.user_id)} onChange={() => toggleSelect(s.user_id)} className="rounded" /></td>
                  <td className="p-3">
                    <p className="font-bold text-xs">{s.name}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">{s.email}</p>
                  </td>
                  <td className="p-3 text-xs">
                    {s.phone && <span className="flex items-center gap-1 text-[var(--text-muted)]"><Phone size={10} />{s.phone}</span>}
                    {s.email && <span className="flex items-center gap-1 text-[var(--text-muted)]"><Mail size={10} />{s.email}</span>}
                  </td>
                  <td className="p-3 text-center text-xs">{lastLogin ? lastLogin.toLocaleDateString('tr-TR') : (lang === 'tr' ? 'Hiç giriş yok' : 'Never')}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${daysAgo > 14 ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                      {daysAgo} {lang === 'tr' ? 'gün' : 'days'}
                    </span>
                  </td>
                </tr>
              );
            })}
            {students.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[var(--text-muted)] text-sm">{lang === 'tr' ? 'İnaktif öğrenci yok' : 'No inactive students'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminContracts() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('vendor');

  return (
    <AdminLayout title="Contracts & Compliance" titleTr={t('admin.con_page_title')}>
      <PageHeader
        title={t('admin.con_page_title')}
        subtitle={t('admin.con_page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.con_page_title') }]}
      />
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl overflow-x-auto" data-testid="contracts-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-white text-[var(--text-main)] shadow-sm' : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'}`}
            data-testid={`tab-${t.id}`}>
            {lang === 'tr' ? t.label_tr : t.label}
          </button>
        ))}
      </div>

      {activeTab === 'vendor' && <VendorContracts lang={lang} authHeaders={authHeaders} />}
      {activeTab === 'student' && <StudentContracts lang={lang} authHeaders={authHeaders} />}
      {activeTab === 'cancellations' && <CancellationsTab lang={lang} authHeaders={authHeaders} />}
      {activeTab === 'inactive' && <InactiveStudents lang={lang} authHeaders={authHeaders} />}
    </AdminLayout>
  );
}
